// Dependencies
import { nanoid } from "nanoid/non-secure";
import React, { useCallback, useMemo, useState } from "react";
import { ActionMeta, components } from "react-select";
import Select from "react-select/async";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faAngleDown, faTimes, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// hooks
import useRequest from "../../../hooks/useRequest";

// utils
import { getOptionComponent } from "./utils";

// Components
import DeletableChip from "../../Chip/templates/DeletableChip";
import { TextSize } from "../../Text";
import InputWrapper from "../InputWrapper";
// Config
import Config from "../SelectInput/Config";
// Styles
import css from "./AjaxInput.module.scss";

export interface BaseProps {
	key?: number | string;
	label?: string;
	labelSize?: TextSize;
	inputId?: string;
	disabled?: boolean;
	placeholder?: string;
	value: string | SelectOption | SelectOption[];
	leftIcon?: IconProp;
	isClearable?: boolean;
	openMenuOnFocus?: boolean;
	fetchOptions?: (inputValue: string) => Promise<SelectOption[]>;
	onChange: (value: SelectOption | SelectOption[], actionMeta: ActionMeta<SelectOption>) => void;
	prependOptions?: SelectOption[];
	isMultiSelect?: boolean;
	filters?: { [key: string]: string };
	showWarning?: boolean;
	type:
		| "department"
		| "templates"
		| "reviewForms"
		| "goalCategories"
		| "goalOutlook"
		| "reviewFormNames"
		| "workflowTemplates";
}

export type SelectOption = {
	label: string;
	value: string;
};

const routeProcessNameLookup = {
	department: "GetGroups",
	templates: "FetchBulkTemplates",
	reviewForms: "getReviewForms",
	workflowTemplates: "GetWorkflowTemplates",
	goalCategories: "getGoalCategories",
	goalOutlook: "getGoalOutlooks",
	reviewFormNames: "getReviewReports",
};

/**
 * We use font awesome to render the dropdown indicator
 */
const DropdownIndicator = (props: any) => {
	return (
		<components.DropdownIndicator {...props}>
			<FontAwesomeIcon icon={faAngleDown} />
		</components.DropdownIndicator>
	);
};

/**
 * We use font awesome to render the clear button
 */
const ClearIndicator = (props: any) => {
	return (
		<components.ClearIndicator {...props}>
			<FontAwesomeIcon icon={faTimes} />
		</components.ClearIndicator>
	);
};

const Menu = (props: any) => {
	return <components.Menu {...props}>{props.children}</components.Menu>;
};

/**
 * This allows copying and pasting the select value
 */
const Input = (props) => <components.Input {...props} isHidden={false} />;

/**
 * Overrides the default ValueContainer component to add a clear button and
 * an icon to the left of the input. The default clear function in react-select
 * does not work the way we want it to, so we override it here. In react-select,
 * the clear button only renders if there is a value, we want it to render if
 * there is a value or if there is text in the input.
 */
const createValueContainer = (createProps: Pick<BaseProps, "leftIcon" | "isClearable">) => {
	const ValueContainer = (props: any) => {
		const handleClearValue = () => {
			props.clearValue();
		};

		const isClearable = createProps.isClearable && (props.selectProps.inputValue.length > 0 || props.hasValue);

		return (
			<components.ValueContainer {...props}>
				{createProps.leftIcon ? (
					<div className={css.searchIcon}>
						<FontAwesomeIcon icon={createProps.leftIcon} />
					</div>
				) : null}
				{props.children}
				{isClearable ? (
					<div className={css.clearText} onClick={handleClearValue}>
						<FontAwesomeIcon icon={faTimesCircle} />
					</div>
				) : null}
			</components.ValueContainer>
		);
	};

	return ValueContainer;
};

/**
 * This is an ajax picker component that uses the react-select library.
 * @param props
 * @returns
 * @deprecated Use the Select/Combobox components from the py-components package instead
 */
const AjaxPicker = (props: BaseProps) => {
	const {
		label,
		labelSize = "paragraph-3",
		value,
		onChange,
		isClearable = false,
		disabled = false,
		placeholder = "Make a selection",
		openMenuOnFocus = false,
		isMultiSelect = false,
		filters,
		showWarning = false,
		key = null,
		type,
	} = props;

	const [inputValue, setInputValue] = useState("");

	const { revalidate } = useRequest(routeProcessNameLookup[type], {}, { skipCache: true, manual: true });

	// We use the nanoid library to generate a unique id for the input. This is
	// used for the label and the input itself to ensure that they are linked
	// correctly.
	const inputId = useMemo(() => {
		return props.inputId || nanoid();
	}, [props.inputId]);

	// Some props can change the styles of the component. We use the useMemo
	// hook to memoize the styles so that we don't have to recompute them on
	// every render.
	const styles = useMemo(() => {
		return Config.customStyles({
			size: "standard",
			leftIcon: props.leftIcon,
			showWarning,
		});
	}, [props.leftIcon, isClearable, showWarning]);

	// The ajax picker needs to fetch the data for a specific data type.
	const fetchOptions = async (inputValue: string) => {
		// allow the default fetch behavior to be overridden
		if (props.fetchOptions) {
			return props.fetchOptions(inputValue);
		}

		const response = await revalidate({ filters: { payload: { name: inputValue }, ...filters } });

		if (response.data && response.data.json) {
			return response.data.json.map((item) => ({ ...item, value: item.id, label: item.name }));
		}
	};

	// In addition to props changing some styles, they can also change the
	// behavior of the component. We override the default ValueContainer
	// component to add the clear button and the left icon.
	const ValueContainer = useCallback(
		createValueContainer({
			isClearable,
			leftIcon: props.leftIcon,
		}),
		[props.leftIcon, isClearable]
	);

	const multiValues = value as SelectOption[];

	// delete function for multivalue options
	const handleDelete = (valToDelete: SelectOption) => {
		const multiValues = (value as SelectOption[]) || [];
		const filteredValues = multiValues.filter((val) => val.value !== valToDelete.value);
		onChange(filteredValues, { action: "remove-value", removedValue: valToDelete });
	};

	// handle the input change when typing
	// setting the input value allows copy and paste
	const onInputChange = (inputValue, { action }) => {
		if (action === "input-change") {
			setInputValue(inputValue);
		}
	};

	// handle selecting an option
	const handleOnChange = (value: SelectOption, actionMeta) => {
		onChange(value, actionMeta);
		setInputValue(value ? value.label : "");
	};

	return (
		<InputWrapper labelSize={labelSize} label={label ? label : ""} inputId={inputId}>
			<Select
				key={key}
				inputId={inputId}
				cacheOptions
				defaultOptions
				styles={styles}
				isDisabled={disabled}
				placeholder={placeholder}
				loadOptions={fetchOptions}
				formatOptionLabel={(optionData) => getOptionComponent(type, optionData)}
				inputValue={inputValue}
				onInputChange={onInputChange}
				components={{
					Menu,
					ValueContainer,
					ClearIndicator,
					DropdownIndicator,
					Input,
				}}
				onChange={handleOnChange as any}
				value={value}
				openMenuOnFocus={openMenuOnFocus}
				isMulti={isMultiSelect}
				controlShouldRenderValue={!isMultiSelect}
			/>
			<div className={`${css.multiSelectOptions}`}>
				{isMultiSelect
					? multiValues.map((value) => (
							<DeletableChip
								className={css.multiValueOption}
								key={value.value}
								label={value.label}
								onDelete={() => handleDelete(value)}
							/>
						))
					: null}
			</div>
		</InputWrapper>
	);
};

/** @deprecated Use the Select/Combobox components from the py-components package instead */
export default AjaxPicker;
